import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BarChart3, TrendingUp, Clock, TriangleAlert, FileText, FileSpreadsheet } from "lucide-react";

const money = (n) => (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + "₺";
const iso = (d) => d.toISOString().slice(0, 10);

async function downloadFile(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([res.data]));
    a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } catch { toast.error("İndirilemedi"); }
}

export default function VanPerformance() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 864e5)));
  const [to, setTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/van-sales/performance", { params: { from_date: from, to } })
      .then((r) => setData(r.data)).catch(() => setData({ drivers: [], totals: {} })).finally(() => setLoading(false));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const rateColor = (r) => r == null ? "text-slate-400" : r >= 80 ? "text-emerald-600" : r >= 50 ? "text-amber-600" : "text-red-600";
  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="van-performance">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-slate-500">Başlangıç</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="perf-from" /></div>
        <div><label className="block text-xs text-slate-500">Bitiş</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="perf-to" /></div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => downloadFile(`/van-sales/performance/export?fmt=pdf&from_date=${from}&to=${to}`, `rota-performans-${from}_${to}.pdf`)}
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="perf-export-pdf"><FileText className="h-3.5 w-3.5 text-red-500" /> PDF</button>
          <button onClick={() => downloadFile(`/van-sales/performance/export?fmt=xlsx&from_date=${from}&to=${to}`, `rota-performans-${from}_${to}.xlsx`)}
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="perf-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" /> Excel</button>
        </div>
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-400"><BarChart3 className="h-3.5 w-3.5" /> Şoför bazında karne — yeşil iyi, kırmızı riskli. Raporu tek tuşla PDF/Excel indirin.</div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["Toplam Ciro", money(t.revenue), "text-emerald-700", TrendingUp],
          ["Verilen İskonto", money(t.discount), "text-amber-600", TriangleAlert],
          ["Kasa Açığı", money(t.cash_shortage), (t.cash_shortage > 0 ? "text-red-600" : "text-slate-700"), TriangleAlert],
          ["Dağıtılan Prim", money(t.prim), "text-sky-700", TrendingUp]].map(([lbl, val, cls, Icon]) => (
          <div key={lbl} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-1 text-[11px] uppercase text-slate-400"><Icon className="h-3 w-3" /> {lbl}</div>
            <div className={`mt-1 text-lg font-bold ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? <div className="py-14 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400">
              <tr><th className="px-3 py-2">Şoför</th><th className="text-right">Satış</th><th className="text-right">Ciro</th><th className="text-right">İskonto</th><th className="text-right">Kasa Açığı</th><th className="text-right">Prim</th><th className="text-center">Zamanında Kontak</th></tr>
            </thead>
            <tbody>
              {(data?.drivers || []).length === 0 && <tr><td colSpan="7" className="py-10 text-center text-slate-400">Bu aralıkta veri yok.</td></tr>}
              {(data?.drivers || []).map((d) => (
                <tr key={d.driver_personnel_id} className="border-t border-slate-50 hover:bg-slate-50/60" data-testid={`perf-row-${d.driver_personnel_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{d.driver_name}</td>
                  <td className="text-right text-slate-600">{d.sales_count}</td>
                  <td className="text-right font-semibold text-emerald-700">{money(d.revenue)}</td>
                  <td className="text-right text-amber-600">{money(d.discount)}</td>
                  <td className={`text-right font-medium ${d.cash_shortage > 0 ? "text-red-600" : "text-slate-400"}`}>{money(d.cash_shortage)}</td>
                  <td className="text-right text-sky-700">{money(d.prim)}</td>
                  <td className="text-center">
                    {d.on_time_rate == null ? <span className="text-slate-300">—</span> : (
                      <span className={`inline-flex items-center gap-1 font-semibold ${rateColor(d.on_time_rate)}`}>
                        <Clock className="h-3.5 w-3.5" />%{d.on_time_rate}
                        {d.ignition_late > 0 && <span className="text-[11px] text-slate-400">({d.avg_late_min}dk geç)</span>}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
